/**
 * CoachPlaybook - Studio Control Panel
 * Version: 0.6.0
 * Description: Right-side control panel for Playbook Studio — play config, formations,
 *              player/defender management, and play library in collapsible accordion sections.
 */

import React, { useState, useRef } from 'react';

/* ─── palette ─── */
const C = {
  panelBg:    '#0f172a',
  surface:    '#1e293b',
  border:     '#334155',
  borderLight:'#475569',
  text:       '#f1f5f9',
  textMuted:  '#94a3b8',
  textDim:    '#64748b',
  accent:     '#3b82f6',
  accentHover:'#2563eb',
  danger:     '#ef4444',
  dangerDim:  '#7f1d1d',
  success:    '#22c55e',
  warning:    '#f59e0b',
  highlight:  '#1d4ed8',
};

/* ─── shared inline helpers ─── */
const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  backgroundColor: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  color: C.text,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
};

const pillStyle = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  border: `1px solid ${active ? C.accent : C.border}`,
  backgroundColor: active ? C.accent + '22' : C.surface,
  color: active ? C.accent : C.textMuted,
  transition: 'all 0.15s',
});

const tactBtnStyle = {
  padding: '7px 6px', fontSize: 11, fontWeight: 600,
  borderRadius: 5, cursor: 'pointer', textAlign: 'center',
  border: `1px solid ${C.border}`,
  background: C.surface, color: C.text,
  transition: 'all 0.12s',
};

/* ─── Section header (accordion) ─── */
const SectionHeader = ({ label, isOpen, onToggle, count }) => (
  <button
    onClick={onToggle}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      backgroundColor: isOpen ? '#1a2744' : 'transparent',
      border: 'none',
      borderBottom: `1px solid ${C.border}`,
      cursor: 'pointer',
      transition: 'background 0.15s',
    }}
  >
    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: C.textMuted }}>
      {label}
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {count !== undefined && (
        <span style={{
          fontSize: 10, fontWeight: 700, color: C.textDim,
          backgroundColor: C.surface, padding: '2px 8px', borderRadius: 999,
        }}>
          {count}
        </span>
      )}
      <span style={{
        fontSize: 10, color: C.textDim,
        transition: 'transform 0.2s',
        display: 'inline-block',
      }}>
        {isOpen ? '▼' : '▶'}
      </span>
    </span>
  </button>
);

/* ══════════════════════════════════════════════════════════
   ControlPanel
   ══════════════════════════════════════════════════════════ */
const ControlPanel = ({
  currentPlay,
  plays = [],
  sportConfig,
  selectedPosition,
  showDefenders = false,
  onPlayUpdate,
  onPlaySelect,
  onPlayCreate,
  onPlayDelete,
  onPositionSelect,
  onFormationApply,
  onToggleDefenders,
  onAssignPlayer,
  selectedDefender,
  showRoutes = true,
  onToggleRoutes,
  showJerseyNumbers = true,
  onToggleJerseyNumbers,
  onExportGif,
  isExporting = false,
  exportProgress = 0,
  onExportImage,
  onExportSummary,
  onExportJson,
  onDefenderSelect,
  spriteSize = 60,
  onSpriteSizeChange,
  colorMode = 'position',
  onColorModeChange,
  currentStep = 0,
  onFacingChange,
  surfaceView = 'full',
  onViewChange,
  sportViews = [],
  lineups = [],
  onSaveLineup,
  onLoadLineup,
  onDeleteLineup,
  onQuickTactic,
  onNextStep,
  addToCurrentStep = false,
  onStepModeChange,
  onScreenRecord,
}) => {
  /* ── local state ── */
  const [sections, setSections] = useState({
    formations: false,
    lineups: false,
    tactics: true,
    players: true,
    defenders: false,
    library: true,
  });
  const [newLineupName, setNewLineupName] = useState('');
  const [newTag, setNewTag] = useState('');
  const [librarySearch, setLibrarySearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editingEventKey, setEditingEventKey] = useState(null); // 'path-0', 'pass-2', etc.
  const scrollRef = useRef(null);

  // Action type options for inline editing
  const PATH_TYPES = [
    { id: 'movement', label: 'Run' },
    { id: 'sprint', label: 'Sprint' },
    { id: 'dribble', label: 'Dribble' },
    { id: 'cut', label: 'Cut' },
  ];
  const PASS_TYPES = [
    { id: 'direct', label: 'Pass', passType: 'direct' },
    { id: 'through-ball', label: 'Through Ball', passType: 'direct' },
    { id: 'lob', label: 'Lob', passType: 'lob' },
    { id: 'cross', label: 'Cross', passType: 'lob' },
    { id: 'shoot', label: 'Shoot', passType: 'direct' },
    { id: 'back-pass', label: 'Back Pass', passType: 'direct' },
  ];

  const changePathType = (pathIdx, newType) => {
    const updated = currentPlay.paths.map((p, i) =>
      i === pathIdx ? { ...p, type: newType, hasBall: newType === 'dribble', lineStyle: newType === 'dribble' ? 'solid' : 'dashed' } : p
    );
    updatePlay({ paths: updated });
    setEditingEventKey(null);
  };

  const changePassType = (passIdx, newType) => {
    const info = PASS_TYPES.find(t => t.id === newType);
    const updated = currentPlay.passes.map((p, i) =>
      i === passIdx ? { ...p, type: newType, passType: info?.passType || 'direct', lineStyle: info?.passType === 'lob' ? 'arc' : 'arrow' } : p
    );
    updatePlay({ passes: updated });
    setEditingEventKey(null);
  };

  const toggle = (key) => setSections((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── derived ── */
  const playTypeGroups = sportConfig?.playTypes || {};
  const allPlayTypes = Object.entries(playTypeGroups).map(([group, items]) => ({ group, items }));
  const formations = sportConfig?.formations || [];
  const positions = currentPlay?.positions || [];
  const defenders = currentPlay?.defenders || [];
  const tags = currentPlay?.tags || [];
  const playType = currentPlay?.type || '';
  const playCategory = currentPlay?.category || '';

  /* ── helpers ── */
  const updatePlay = (patch) => onPlayUpdate?.({ ...currentPlay, ...patch });

  const addTag = () => {
    const t = newTag.trim();
    if (!t || tags.includes(t)) return;
    updatePlay({ tags: [...tags, t] });
    setNewTag('');
  };

  const removeTag = (tag) => updatePlay({ tags: tags.filter((t) => t !== tag) });

  const filteredPlays = plays
    .filter((p) => {
      if (!librarySearch) return true;
      const q = librarySearch.toLowerCase();
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.type || '').toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => (a.playNumber || 0) - (b.playNumber || 0));

  /* ── type badge color map ── */
  const typeBadgeColor = (type) => {
    const map = { offense: '#3b82f6', defense: '#ef4444', outOfBounds: '#f59e0b', special: '#a855f7', transition: '#06b6d4' };
    return map[type] || C.textDim;
  };

  /* ═══════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════ */
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: C.panelBg,
        borderLeft: `1px solid ${C.border}`,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: C.text,
        overflow: 'hidden',
      }}
    >
      {/* ─── scrollable body ─── */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: `${C.border} ${C.panelBg}`,
        }}
      >
        {/* ════════════════════════════════════════════════
           1. PLAY INFO  (always visible, not collapsible)
           ════════════════════════════════════════════════ */}
        <div style={{ padding: 16, borderBottom: `1px solid ${C.border}` }}>
          {/* Play name */}
          <input
            type="text"
            value={currentPlay?.name || ''}
            onChange={(e) => updatePlay({ name: e.target.value })}
            placeholder="Play name..."
            style={{ ...inputStyle, fontSize: 15, fontWeight: 600, marginBottom: 12 }}
          />

          {/* Sport badge */}
          {sportConfig && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 999,
                backgroundColor: (sportConfig.accentColor || C.accent) + '22',
                border: `1px solid ${(sportConfig.accentColor || C.accent) + '44'}`,
                fontSize: 12, fontWeight: 600, color: sportConfig.accentColor || C.accent,
              }}>
                {sportConfig.icon || '🏟️'} {sportConfig.name || 'Sport'}
              </span>
            </div>
          )}

          {/* Field view toggle */}
          {onViewChange && sportViews.length > 1 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
                Field View
              </label>
              <div style={{ display: 'flex', gap: 4 }}>
                {sportViews.map(v => (
                  <button key={v} onClick={() => onViewChange(v)} style={{
                    flex: 1, padding: '5px 0', fontSize: 11, fontWeight: 600,
                    borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${surfaceView === v ? C.accent : C.border}`,
                    background: surfaceView === v ? C.accent + '22' : 'transparent',
                    color: surfaceView === v ? C.accent : C.textDim,
                    textTransform: 'capitalize',
                  }}>{v === 'full' ? 'Full Pitch' : v === 'half' ? 'Half Pitch' : v.replace(/-/g, ' ')}</button>
                ))}
              </div>
            </div>
          )}

          {/* Type selector */}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
            Type
          </label>
          <select
            value={playType}
            onChange={(e) => updatePlay({ type: e.target.value, category: '' })}
            style={{ ...inputStyle, marginBottom: 12, cursor: 'pointer' }}
          >
            <option value="">Select type...</option>
            {allPlayTypes.map(({ group }) => (
              <option key={group} value={group}>
                {group.charAt(0).toUpperCase() + group.slice(1).replace(/([A-Z])/g, ' $1')}
              </option>
            ))}
          </select>

          {/* Category selector (driven by selected type) */}
          {playType && playTypeGroups[playType] && (
            <>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
                Category
              </label>
              <select
                value={playCategory}
                onChange={(e) => updatePlay({ category: e.target.value })}
                style={{ ...inputStyle, marginBottom: 12, cursor: 'pointer' }}
              >
                <option value="">Select category...</option>
                {playTypeGroups[playType].map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </>
          )}

          {/* Tags */}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
            Tags
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {tags.map((tag) => (
              <span key={tag} style={pillStyle(false)}>
                {tag}
                <span
                  onClick={() => removeTag(tag)}
                  style={{ cursor: 'pointer', color: C.danger, fontWeight: 700, marginLeft: 2 }}
                >
                  ×
                </span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
              placeholder="Add tag..."
              style={{ ...inputStyle, flex: 1, fontSize: 12 }}
            />
            <button
              onClick={addTag}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none',
                backgroundColor: C.accent, color: '#fff', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', opacity: newTag.trim() ? 1 : 0.4,
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* ════════════════════════════════════════════════
           2. FORMATIONS
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Formations" isOpen={sections.formations} onToggle={() => toggle('formations')} count={formations.length} />
        {sections.formations && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {formations.map((f) => {
                const isActive = currentPlay?.formation === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => onFormationApply?.(f)}
                    style={{
                      padding: '10px 8px',
                      backgroundColor: isActive ? C.accent + '22' : C.surface,
                      border: `1px solid ${isActive ? C.accent : C.border}`,
                      borderRadius: 8,
                      color: isActive ? C.accent : C.text,
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 500,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = C.border;
                        e.currentTarget.style.borderColor = C.borderLight;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = C.surface;
                        e.currentTarget.style.borderColor = C.border;
                      }
                    }}
                  >
                    {f.name}
                    {f.type && (
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
                        {f.type}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {formations.length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: C.textDim, fontSize: 12 }}>
                No formations defined for this sport
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           2.5 LINEUPS
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Lineups" isOpen={sections.lineups} onToggle={() => toggle('lineups')} count={lineups.length} />
        {sections.lineups && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {/* Save current lineup */}
            {onSaveLineup && currentPlay && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <input
                  type="text"
                  value={newLineupName}
                  onChange={(e) => setNewLineupName(e.target.value)}
                  placeholder="Lineup name..."
                  style={{ ...inputStyle, flex: 1, padding: '6px 10px', fontSize: 12 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newLineupName.trim()) {
                      onSaveLineup(newLineupName.trim());
                      setNewLineupName('');
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (newLineupName.trim()) {
                      onSaveLineup(newLineupName.trim());
                      setNewLineupName('');
                    }
                  }}
                  disabled={!newLineupName.trim()}
                  style={{
                    padding: '6px 12px', fontSize: 11, fontWeight: 600,
                    borderRadius: 6, cursor: newLineupName.trim() ? 'pointer' : 'default',
                    border: `1px solid ${C.accent}`,
                    background: C.accent + '22', color: C.accent,
                    opacity: newLineupName.trim() ? 1 : 0.4,
                  }}
                >Save</button>
              </div>
            )}

            {/* Saved lineups list */}
            {lineups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 16, color: C.textDim, fontSize: 12 }}>
                No saved lineups — assign players then save
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {lineups.map((lineup) => (
                  <div key={lineup.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 10px', borderRadius: 6,
                    backgroundColor: C.surface, border: `1px solid ${C.border}`,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lineup.name}
                      </div>
                      <div style={{ fontSize: 10, color: C.textDim }}>
                        {lineup.assignments.filter(a => a.playerName).length} players assigned
                      </div>
                    </div>
                    <button
                      onClick={() => onLoadLineup?.(lineup)}
                      style={{
                        padding: '3px 10px', fontSize: 10, fontWeight: 600,
                        borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${C.accent}`,
                        background: C.accent + '22', color: C.accent,
                      }}
                    >Apply</button>
                    <button
                      onClick={() => onDeleteLineup?.(lineup.id)}
                      style={{
                        padding: '3px 6px', fontSize: 10,
                        borderRadius: 4, cursor: 'pointer',
                        border: `1px solid ${C.border}`,
                        background: 'transparent', color: C.textDim,
                      }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           2.7 QUICK TACTICS
           ════════════════════════════════════════════════ */}
        {currentPlay && onQuickTactic && (
          <>
            <SectionHeader label="Quick Tactics" isOpen={sections.tactics} onToggle={() => toggle('tactics')} />
            {sections.tactics && (
              <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
                {/* Facing buttons */}
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  Facing
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 10 }}>
                  <button onClick={() => onQuickTactic('face-ball')} style={tactBtnStyle}>
                    Face Ball
                  </button>
                  <button onClick={() => onQuickTactic('face-opponent-goal')} style={tactBtnStyle}>
                    Face Opp. Goal
                  </button>
                  <button onClick={() => onQuickTactic('face-own-goal')} style={tactBtnStyle}>
                    Face Own Goal
                  </button>
                  <button onClick={() => onQuickTactic('face-auto')} style={tactBtnStyle}>
                    Auto (Clear All)
                  </button>
                </div>

                {/* Movement buttons */}
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                  Movement
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  <button onClick={() => onQuickTactic('crash-goal')} style={{ ...tactBtnStyle, borderColor: '#f59e0b44', color: '#f59e0b', backgroundColor: '#f59e0b11' }}>
                    Crash the Goal
                  </button>
                  <button onClick={() => onQuickTactic('defense-up')} style={{ ...tactBtnStyle, borderColor: '#ef444444', color: '#ef4444', backgroundColor: '#ef444411' }}>
                    Defense Up
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════
           3. PLAYERS
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Players" isOpen={sections.players} onToggle={() => toggle('players')} count={positions.length} />
        {sections.players && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {/* Team Kit Selection */}
            {onPlayUpdate && currentPlay && (() => {
              const COLORS = ['white','black','red','blue','navy','green','yellow','orange','purple','pink','grey','teal'];
              const COLOR_HEX = {
                white:'#f0f0f0', black:'#141414', red:'#d91e1e', blue:'#264adb', navy:'#0d1a59',
                green:'#1a8c33', yellow:'#f2d919', orange:'#f27f19', purple:'#7326b3',
                pink:'#e5669a', grey:'#808080', teal:'#1a99a6'
              };
              const teamJersey = currentPlay.teamJerseyPreset || 'blue';
              const teamShorts = currentPlay.teamShortsPreset || 'white';
              const gkJersey = currentPlay.gkJerseyPreset || 'purple';
              const gkShorts = currentPlay.gkShortsPreset || 'purple';

              const ColorRow = ({ label, value, onChange }) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <span style={{ color: C.textDim, fontSize: 10, minWidth: 45 }}>{label}</span>
                  <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {COLORS.map(c => (
                      <div key={c} onClick={() => onChange(c)} style={{
                        width: 14, height: 14, borderRadius: 3, cursor: 'pointer',
                        backgroundColor: COLOR_HEX[c],
                        border: value === c ? '2px solid #fff' : '1px solid rgba(255,255,255,0.15)',
                        boxShadow: value === c ? `0 0 4px ${COLOR_HEX[c]}` : 'none',
                      }} title={c} />
                    ))}
                  </div>
                </div>
              );

              const updateKit = (field, val) => {
                const updated = { ...currentPlay, [field]: val, lastModified: new Date().toISOString() };
                // Apply to all positions
                const isGkField = field.startsWith('gk');
                updated.positions = updated.positions.map(p => {
                  const isGK = p.positionLabel === 'GK' || p.position === 'GK';
                  if (isGK && isGkField) {
                    return { ...p,
                      jerseyColorPreset: field === 'gkJerseyPreset' ? val : (updated.gkJerseyPreset || gkJersey),
                      shortsColorPreset: field === 'gkShortsPreset' ? val : (updated.gkShortsPreset || gkShorts),
                    };
                  } else if (!isGK && !isGkField) {
                    return { ...p,
                      jerseyColorPreset: field === 'teamJerseyPreset' ? val : (updated.teamJerseyPreset || teamJersey),
                      shortsColorPreset: field === 'teamShortsPreset' ? val : (updated.teamShortsPreset || teamShorts),
                    };
                  }
                  return p;
                });
                onPlayUpdate(updated);
              };

              return (
                <div style={{ marginBottom: 10, padding: '4px 0' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                    Team Kit
                  </div>
                  <ColorRow label="Jersey" value={teamJersey} onChange={(c) => updateKit('teamJerseyPreset', c)} />
                  <ColorRow label="Shorts" value={teamShorts} onChange={(c) => updateKit('teamShortsPreset', c)} />
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 8, marginBottom: 6 }}>
                    GK Kit
                  </div>
                  <ColorRow label="Jersey" value={gkJersey} onChange={(c) => updateKit('gkJerseyPreset', c)} />
                  <ColorRow label="Shorts" value={gkShorts} onChange={(c) => updateKit('gkShortsPreset', c)} />
                </div>
              );
            })()}

            {/* Place ball button */}
            {onPlayUpdate && currentPlay && !currentPlay.ballPosition && (
              <button
                onClick={() => {
                  const ballHolder = positions.find(p => p.hasBall);
                  const bx = ballHolder?.startX || 50;
                  const by = ballHolder?.startY || 50;
                  onPlayUpdate({
                    ...currentPlay,
                    ballPosition: { x: bx, y: by },
                    positions: currentPlay.positions.map(p => ({ ...p, hasBall: false })),
                    lastModified: new Date().toISOString(),
                  });
                }}
                style={{
                  width: '100%', padding: '6px 0', marginBottom: 8,
                  background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)',
                  borderRadius: 6, color: '#fbbf24', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}
              >⚽ Place Ball on Field</button>
            )}
            {currentPlay?.ballPosition && (
              <button
                onClick={() => {
                  // Give ball back to first player
                  onPlayUpdate({
                    ...currentPlay,
                    ballPosition: null,
                    positions: currentPlay.positions.map((p, i) => ({ ...p, hasBall: i === 0 })),
                    lastModified: new Date().toISOString(),
                  });
                }}
                style={{
                  width: '100%', padding: '6px 0', marginBottom: 8,
                  background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
                  borderRadius: 6, color: '#60a5fa', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}
              >↩ Give Ball to Player</button>
            )}

            {/* Show/Hide toggles */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
              {onToggleRoutes && (
                <button
                  onClick={onToggleRoutes}
                  style={{
                    flex: 1, padding: '5px 0', fontSize: 10, fontWeight: 600,
                    borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${showRoutes ? C.accent : C.border}`,
                    background: showRoutes ? C.accent + '22' : 'transparent',
                    color: showRoutes ? C.accent : C.textDim,
                  }}
                >{showRoutes ? 'Routes On' : 'Routes Off'}</button>
              )}
              {onToggleJerseyNumbers && (
                <button
                  onClick={onToggleJerseyNumbers}
                  style={{
                    flex: 1, padding: '5px 0', fontSize: 10, fontWeight: 600,
                    borderRadius: 4, cursor: 'pointer',
                    border: `1px solid ${showJerseyNumbers ? C.accent : C.border}`,
                    background: showJerseyNumbers ? C.accent + '22' : 'transparent',
                    color: showJerseyNumbers ? C.accent : C.textDim,
                  }}
                >{showJerseyNumbers ? '# Numbers On' : '# Numbers Off'}</button>
              )}
            </div>

            {/* Sprite size */}
            {onSpriteSizeChange && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ color: C.textDim, fontSize: 10, flexShrink: 0 }}>Size</span>
                <input
                  type="range" min="10" max="120" step="5"
                  value={spriteSize}
                  onChange={(e) => onSpriteSizeChange(Number(e.target.value))}
                  style={{ flex: 1, accentColor: C.accent }}
                />
                <span style={{ color: C.textMuted, fontSize: 10, minWidth: 20 }}>{spriteSize}</span>
              </div>
            )}
            {positions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: C.textDim, fontSize: 12 }}>
                No players on the surface
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {positions.map((pos) => {
                  const isSelected = selectedPosition === pos.id;
                  const hasBall = pos.hasBall || false;
                  return (
                    <button
                      key={pos.id}
                      onClick={() => onPositionSelect?.(pos.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        backgroundColor: isSelected ? C.accent + '18' : 'transparent',
                        border: 'none',
                        borderLeft: `3px solid ${isSelected ? C.accent : 'transparent'}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = C.surface; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* color dot */}
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%',
                        backgroundColor: pos.color || C.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                        boxShadow: isSelected ? `0 0 0 2px ${C.accent}` : 'none',
                      }}>
                        {pos.positionLabel || '?'}
                      </span>

                      {/* info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pos.playerName || pos.positionLabel || `Player ${pos.id}`}
                        </div>
                        <div style={{ fontSize: 10, color: C.textDim }}>
                          {pos.jerseyNumber ? `#${pos.jerseyNumber}` : pos.positionLabel || ''}
                        </div>
                      </div>

                      {/* ball possession toggle */}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onPlayUpdate && currentPlay) {
                            onPlayUpdate({
                              ...currentPlay,
                              ballPosition: null,
                              positions: currentPlay.positions.map(p => ({ ...p, hasBall: p.id === pos.id })),
                              lastModified: new Date().toISOString(),
                            });
                          }
                        }}
                        style={{
                          width: 18, height: 18, borderRadius: '50%',
                          backgroundColor: hasBall ? C.warning : 'transparent',
                          border: hasBall ? 'none' : `1px solid ${C.border}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, cursor: 'pointer', flexShrink: 0,
                          boxShadow: hasBall ? `0 0 6px ${C.warning}` : 'none',
                        }}
                        title={hasBall ? 'Has ball' : 'Give ball'}
                      >
                        {hasBall ? '⚽' : ''}
                      </span>

                      {/* Assign from roster */}
                      {onAssignPlayer && (
                        <span
                          onClick={(e) => { e.stopPropagation(); onAssignPlayer(pos.id); }}
                          style={{
                            fontSize: 10, color: C.textDim, cursor: 'pointer',
                            padding: '2px 6px', borderRadius: 4,
                            border: `1px solid ${C.border}`,
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textDim; }}
                          title="Assign player from roster"
                        >
                          {pos.playerId ? '✎' : '+ Assign'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Selected Player Settings (speed, facing, compass) ── */}
            {(selectedPosition || selectedDefender) && (() => {
              const selPos = selectedPosition
                ? positions.find(p => p.id === selectedPosition)
                : defenders.find(d => d.id === selectedDefender);
              if (!selPos) return null;
              const speed = selPos.speed || 1.0;
              const selLabel = selPos.playerName || selPos.positionLabel || '?';

              return (
                <div style={{
                  marginTop: 10, padding: 10, borderRadius: 6,
                  backgroundColor: C.accent + '08',
                  border: `1px solid ${C.accent}33`,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    {selLabel} Settings
                  </div>

                  {/* Speed */}
                  {selectedPosition && onPlayUpdate && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: C.textDim, flexShrink: 0 }}>Speed</span>
                      <input
                        type="range" min="0.25" max="3.0" step="0.25"
                        value={speed}
                        onChange={(e) => {
                          const newSpeed = parseFloat(e.target.value);
                          const updated = {
                            ...currentPlay,
                            positions: currentPlay.positions.map(p =>
                              p.id === selectedPosition ? { ...p, speed: newSpeed } : p
                            ),
                            lastModified: new Date().toISOString(),
                          };
                          onPlayUpdate(updated);
                        }}
                        style={{ flex: 1, accentColor: C.accent }}
                      />
                      <span style={{ fontSize: 10, color: C.textMuted, minWidth: 30 }}>{speed}x</span>
                    </div>
                  )}

                  {/* Facing Direction Compass */}
                  {onFacingChange && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: C.textDim }}>Facing Override</span>
                        <button
                          onClick={() => {
                            onFacingChange(selectedPosition || selectedDefender, null);
                          }}
                          style={{
                            fontSize: 9, color: C.textDim, cursor: 'pointer',
                            padding: '1px 6px', borderRadius: 3,
                            border: `1px solid ${C.border}`, background: 'transparent',
                          }}
                        >Auto</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, maxWidth: 120 }}>
                        {[
                          { dir: 'NE', label: '↖' }, { dir: 'N', label: '↑' }, { dir: 'NW', label: '↗' },
                          { dir: 'E', label: '←' },  { dir: null, label: '·' }, { dir: 'W', label: '→' },
                          { dir: 'SE', label: '↙' }, { dir: 'S', label: '↓' }, { dir: 'SW', label: '↘' },
                        ].map(({ dir, label }, i) => (
                          <button key={i}
                            onClick={() => dir && onFacingChange(selectedPosition || selectedDefender, dir)}
                            disabled={!dir}
                            style={{
                              width: 32, height: 28, borderRadius: 4, border: `1px solid ${C.border}`,
                              background: selPos.facingDirection === dir ? C.accent + '33' : 'transparent',
                              color: dir ? C.text : 'transparent',
                              fontSize: 14, cursor: dir ? 'pointer' : 'default',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >{label}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Step controls */}
        {currentPlay && onNextStep && (
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: C.textMuted, flex: 1 }}>
                Adding to <strong style={{ color: C.accent }}>Step {currentStep}</strong>
              </span>
              <button
                onClick={onNextStep}
                style={{
                  padding: '6px 14px', fontSize: 11, fontWeight: 700,
                  borderRadius: 5, cursor: 'pointer',
                  border: `1px solid #22c55e`,
                  background: 'rgba(34,197,94,0.15)',
                  color: '#4ade80',
                }}
                onMouseEnter={ev => { ev.currentTarget.style.background = 'rgba(34,197,94,0.3)'; }}
                onMouseLeave={ev => { ev.currentTarget.style.background = 'rgba(34,197,94,0.15)'; }}
              >Next Step →</button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           3.5 PLAY NARRATION
           ════════════════════════════════════════════════ */}
        {currentPlay && ((currentPlay.paths || []).length > 0 || (currentPlay.passes || []).length > 0) && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Play Description
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.6 }}>
              {(() => {
                const allEvents = [];
                const firstName = (name) => (name || '').split(' ')[0] || '?';
                (currentPlay.paths || []).forEach((path, idx) => {
                  const player = positions.find(p => p.id === path.positionId);
                  const label = firstName(player?.playerName) || player?.positionLabel || '?';
                  const typeLabel = path.type === 'dribble' ? 'dribbles' : path.type === 'sprint' ? 'sprints' : 'runs';
                  allEvents.push({ step: path.stepNumber, text: `${label} ${typeLabel} forward`, kind: 'path', idx });
                });
                (currentPlay.passes || []).forEach((pass, idx) => {
                  const from = positions.find(p => p.id === pass.fromPositionId);
                  const to = positions.find(p => p.id === pass.toPositionId);
                  const fromLabel = firstName(from?.playerName) || from?.positionLabel || '?';
                  const toLabel = to ? (firstName(to.playerName) || to.positionLabel) : 'open space';
                  const typeLabel = pass.type === 'shoot' ? 'shoots'
                    : pass.type === 'through-ball' ? 'plays through ball'
                    : pass.type === 'cross' ? 'crosses'
                    : pass.type === 'lob' ? 'lobs' : 'passes';
                  const target = pass.toPositionId ? `to ${toLabel}` : 'toward goal';
                  allEvents.push({ step: pass.stepNumber, text: `${fromLabel} ${typeLabel} ${target}`, kind: 'pass', idx });
                });
                allEvents.sort((a, b) => a.step - b.step);
                // Assign sub-step labels: 1a, 1b, 1c...
                let lastStep = -1;
                let subIdx = 0;
                allEvents.forEach(e => {
                  if (e.step !== lastStep) { lastStep = e.step; subIdx = 0; }
                  e.subLabel = String.fromCharCode(97 + subIdx); // a, b, c...
                  subIdx++;
                });
                const removeEvent = (e) => {
                  if (e.kind === 'path') {
                    const path = currentPlay.paths[e.idx];
                    const playerId = path?.positionId;
                    const updatedPaths = currentPlay.paths.filter((_, i) => i !== e.idx);
                    if (playerId) {
                      // Find the last path for this player in a previous step
                      const priorPaths = updatedPaths
                        .filter(p => p.positionId === playerId && p.stepNumber < path.stepNumber)
                        .sort((a, b) => b.stepNumber - a.stepNumber);
                      const player = positions.find(p => p.id === playerId);
                      if (player) {
                        let resetX, resetY;
                        if (priorPaths.length > 0) {
                          // Snap to end of their last path in a prior step
                          const lastPath = priorPaths[0];
                          const lastPt = lastPath.points?.[lastPath.points.length - 1];
                          resetX = lastPt?.x ?? player.startX ?? player.x;
                          resetY = lastPt?.y ?? player.startY ?? player.y;
                        } else {
                          // No prior paths — back to starting position
                          resetX = player.startX ?? player.x;
                          resetY = player.startY ?? player.y;
                        }
                        const updatedPositions = positions.map(p =>
                          p.id === playerId ? { ...p, x: resetX, y: resetY } : p
                        );
                        updatePlay({ paths: updatedPaths, positions: updatedPositions });
                      } else {
                        updatePlay({ paths: updatedPaths });
                      }
                    } else {
                      updatePlay({ paths: updatedPaths });
                    }
                  } else {
                    updatePlay({ passes: currentPlay.passes.filter((_, i) => i !== e.idx) });
                  }
                };
                return allEvents.map((e, i) => {
                  const eventKey = `${e.kind}-${e.idx}`;
                  const isEditing = editingEventKey === eventKey;
                  const options = e.kind === 'path' ? PATH_TYPES : PASS_TYPES;
                  const currentType = e.kind === 'path'
                    ? (currentPlay.paths[e.idx]?.type || 'movement')
                    : (currentPlay.passes[e.idx]?.type || 'direct');

                  return (
                    <div key={i} style={{ marginBottom: isEditing ? 4 : 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        color: e.step === currentStep ? C.text : C.textDim,
                        fontWeight: e.step === currentStep ? 600 : 400,
                        padding: '2px 0',
                      }}>
                        <span style={{ color: C.accent, fontSize: 10, minWidth: 20 }}>{e.step}{e.subLabel}.</span>
                        <span
                          style={{ flex: 1, cursor: 'pointer', borderBottom: '1px dotted transparent' }}
                          onMouseEnter={ev => ev.currentTarget.style.borderBottomColor = C.accent}
                          onMouseLeave={ev => ev.currentTarget.style.borderBottomColor = 'transparent'}
                          onClick={() => setEditingEventKey(isEditing ? null : eventKey)}
                          title="Click to change action type"
                        >{e.text}</span>
                        <button
                          onClick={() => removeEvent(e)}
                          style={{
                            background: 'none', border: 'none', color: C.danger, cursor: 'pointer',
                            fontSize: 11, padding: '0 4px', opacity: 0.6, lineHeight: 1,
                          }}
                          onMouseEnter={ev => ev.currentTarget.style.opacity = '1'}
                          onMouseLeave={ev => ev.currentTarget.style.opacity = '0.6'}
                          title="Remove this action"
                        >✕</button>
                      </div>
                      {isEditing && (
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: 3, padding: '4px 0 4px 20px',
                        }}>
                          {options.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => e.kind === 'path' ? changePathType(e.idx, opt.id) : changePassType(e.idx, opt.id)}
                              style={{
                                padding: '3px 8px', fontSize: 10, fontWeight: 600,
                                borderRadius: 4, cursor: 'pointer',
                                border: `1px solid ${currentType === opt.id ? C.accent : C.border}`,
                                background: currentType === opt.id ? C.accent + '33' : C.surface,
                                color: currentType === opt.id ? C.accent : C.textMuted,
                              }}
                            >{opt.label}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════
           4. DEFENDERS (hidden for sports that don't use defender overlay)
           ════════════════════════════════════════════════ */}
        {!sportConfig?.hideDefenderToggle && (
        <SectionHeader label="Defenders" isOpen={sections.defenders} onToggle={() => toggle('defenders')} count={defenders.length} />
        )}
        {sections.defenders && !sportConfig?.hideDefenderToggle && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {/* Toggle row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', backgroundColor: C.surface, borderRadius: 6, marginBottom: 10,
            }}>
              <span style={{ fontSize: 12, color: C.text }}>Show Defenders</span>
              <button
                onClick={() => onToggleDefenders?.()}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  backgroundColor: showDefenders ? C.danger : C.border,
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                }}
              >
                <span style={{
                  position: 'absolute', top: 2,
                  left: showDefenders ? 20 : 2,
                  width: 18, height: 18, borderRadius: '50%',
                  backgroundColor: '#fff', transition: 'left 0.2s',
                }} />
              </button>
            </div>

            {/* Defender Kit — outfield + GK separate */}
            {onPlayUpdate && currentPlay && (() => {
              const COLORS = ['white','black','red','blue','navy','green','yellow','orange','purple','pink','grey','teal'];
              const COLOR_HEX = {
                white:'#f0f0f0', black:'#141414', red:'#d91e1e', blue:'#264adb', navy:'#0d1a59',
                green:'#1a8c33', yellow:'#f2d919', orange:'#f27f19', purple:'#7326b3',
                pink:'#e5669a', grey:'#808080', teal:'#1a99a6'
              };
              const defJersey = currentPlay.defJerseyPreset || 'red';
              const defShorts = currentPlay.defShortsPreset || 'black';
              const defGkJersey = currentPlay.defGkJerseyPreset || 'yellow';
              const defGkShorts = currentPlay.defGkShortsPreset || 'black';

              const updateDefKit = (field, val) => {
                const updated = { ...currentPlay, [field]: val, lastModified: new Date().toISOString() };
                const isGkField = field.startsWith('defGk');
                updated.defenders = (updated.defenders || []).map(d => {
                  const isGK = d.positionLabel === 'GK' || d.label === 'GK';
                  if (isGK && isGkField) {
                    return { ...d,
                      jerseyColorPreset: field === 'defGkJerseyPreset' ? val : (updated.defGkJerseyPreset || defGkJersey),
                      shortsColorPreset: field === 'defGkShortsPreset' ? val : (updated.defGkShortsPreset || defGkShorts),
                    };
                  } else if (!isGK && !isGkField) {
                    return { ...d,
                      jerseyColorPreset: field === 'defJerseyPreset' ? val : (updated.defJerseyPreset || defJersey),
                      shortsColorPreset: field === 'defShortsPreset' ? val : (updated.defShortsPreset || defShorts),
                    };
                  }
                  return d;
                });
                onPlayUpdate(updated);
              };

              const ColorRow = ({ label, value, onChange }) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                  <span style={{ color: C.textDim, fontSize: 10, minWidth: 45 }}>{label}</span>
                  <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {COLORS.map(c => (
                      <div key={c} onClick={() => onChange(c)} style={{
                        width: 14, height: 14, borderRadius: 3, cursor: 'pointer',
                        backgroundColor: COLOR_HEX[c],
                        border: value === c ? '2px solid #fff' : '1px solid rgba(255,255,255,0.15)',
                        boxShadow: value === c ? `0 0 4px ${COLOR_HEX[c]}` : 'none',
                      }} title={c} />
                    ))}
                  </div>
                </div>
              );

              return (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                    Opponent Kit
                  </div>
                  <ColorRow label="Jersey" value={defJersey} onChange={(c) => updateDefKit('defJerseyPreset', c)} />
                  <ColorRow label="Shorts" value={defShorts} onChange={(c) => updateDefKit('defShortsPreset', c)} />
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 8, marginBottom: 4 }}>
                    Opponent GK Kit
                  </div>
                  <ColorRow label="Jersey" value={defGkJersey} onChange={(c) => updateDefKit('defGkJerseyPreset', c)} />
                  <ColorRow label="Shorts" value={defGkShorts} onChange={(c) => updateDefKit('defGkShortsPreset', c)} />
                </div>
              );
            })()}

            {/* Defender list */}
            {defenders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: C.textDim, fontSize: 12 }}>
                No defenders
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {defenders.map((def, idx) => {
                  const guardedPlayer = positions.find((p) => p.id === def.guardingId);
                  const isSelected = selectedDefender === def.id;
                  const defPreset = def.jerseyColorPreset || currentPlay?.defJerseyPreset || 'red';
                  const defShortsP = def.shortsColorPreset || currentPlay?.defShortsPreset || 'black';
                  return (
                    <button
                      key={def.id}
                      onClick={() => onDefenderSelect?.(def.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: 4,
                        backgroundColor: isSelected ? C.danger + '18' : 'transparent',
                        border: 'none', borderLeft: `3px solid ${isSelected ? C.danger : 'transparent'}`,
                        cursor: 'pointer', width: '100%', textAlign: 'left',
                        transition: 'background 0.12s',
                      }}
                    >
                      {/* Sprite mini preview */}
                      <img
                        src={`/sprites/color/${defPreset}_${defShortsP}/player_S.png`}
                        alt=""
                        style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                          {def.positionLabel || def.label || `D${idx + 1}`}
                        </div>
                        <div style={{ fontSize: 10, color: C.textDim }}>
                          {guardedPlayer ? `vs ${guardedPlayer.playerName || guardedPlayer.positionLabel}` : ''}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           5. PLAY LIBRARY
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Play Library" isOpen={sections.library} onToggle={() => toggle('library')} count={plays.length} />
        {sections.library && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {/* New play button */}
            <button
              onClick={() => onPlayCreate?.()}
              style={{
                width: '100%', padding: '8px 0', marginBottom: 10,
                backgroundColor: C.accent, border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.accentHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = C.accent; }}
            >
              + New Play
            </button>

            {/* Search */}
            <input
              type="text"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder="Search plays..."
              style={{ ...inputStyle, fontSize: 12, marginBottom: 10 }}
            />

            {/* Play list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${C.border} transparent` }}>
              {filteredPlays.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: C.textDim, fontSize: 12 }}>
                  {plays.length === 0 ? 'No saved plays' : 'No matching plays'}
                </div>
              ) : (
                filteredPlays.map((play) => {
                  const isCurrent = currentPlay?.id === play.id;
                  return (
                    <div
                      key={play.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px',
                        backgroundColor: isCurrent ? C.accent + '18' : 'transparent',
                        borderLeft: `3px solid ${isCurrent ? C.accent : 'transparent'}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                      }}
                      onClick={() => onPlaySelect?.(play.id)}
                      onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = C.surface; }}
                      onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = isCurrent ? C.accent + '18' : 'transparent'; }}
                    >
                      {/* play number */}
                      {play.playNumber && (
                        <span style={{
                          width: 24, height: 24, borderRadius: '50%',
                          backgroundColor: isCurrent ? C.accent + '33' : C.surface,
                          border: `1px solid ${isCurrent ? C.accent : C.border}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: isCurrent ? C.accent : C.textMuted,
                          flexShrink: 0,
                        }}>
                          {play.playNumber}
                        </span>
                      )}
                      {/* info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {play.name || 'Untitled'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          {play.type && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                              padding: '2px 6px', borderRadius: 3,
                              backgroundColor: typeBadgeColor(play.type) + '22',
                              color: typeBadgeColor(play.type),
                            }}>
                              {play.type}
                            </span>
                          )}
                          <span style={{ fontSize: 10, color: C.textDim }}>
                            {(() => {
                              const ts = play.createdAt || play.lastModified;
                              if (!ts) return '';
                              const d = new Date(ts);
                              return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                            })()}
                          </span>
                        </div>
                      </div>

                      {/* delete */}
                      {confirmDeleteId === play.id ? (
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); onPlayDelete?.(play.id); setConfirmDeleteId(null); }}
                            style={{
                              padding: '4px 10px', borderRadius: 4, border: 'none',
                              backgroundColor: C.danger, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Delete
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                            style={{
                              padding: '4px 8px', borderRadius: 4, border: `1px solid ${C.border}`,
                              backgroundColor: 'transparent', color: C.textMuted, fontSize: 11, cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(play.id); }}
                          style={{
                            padding: '4px 8px', borderRadius: 4, border: 'none',
                            backgroundColor: 'transparent', color: C.textDim, fontSize: 14,
                            cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = C.danger; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = C.textDim; }}
                          title="Delete play"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── footer version ─── */}
      <div style={{
        padding: '6px 16px',
        borderTop: `1px solid ${C.border}`,
        fontSize: 10,
        color: C.textDim,
        textAlign: 'center',
        fontFamily: 'monospace',
        flexShrink: 0,
      }}>
        v0.6.0
      </div>

      {/* Export / Share */}
      {currentPlay && (
        <div style={{ padding: 12, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Export / Share
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {onExportGif && (
              <button onClick={onExportGif} disabled={isExporting} style={{
                width: '100%', padding: '8px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: isExporting ? 'wait' : 'pointer',
                background: isExporting ? 'rgba(251,191,36,0.3)' : 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24',
              }}>{isExporting ? `🎬 Recording... ${exportProgress}%` : '🎬 Export Animated GIF'}</button>
            )}
            {onExportImage && (
              <button onClick={onExportImage} style={{
                width: '100%', padding: '6px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.25)', color: '#a78bfa',
              }}>📸 Export as Image</button>
            )}
            {onExportSummary && (
              <button onClick={onExportSummary} style={{
                width: '100%', padding: '6px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: '#60a5fa',
              }}>📋 Copy Play Summary</button>
            )}
            {onExportJson && (
              <button onClick={onExportJson} style={{
                width: '100%', padding: '6px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', color: '#4ade80',
              }}>💾 Export Play JSON</button>
            )}
            {onScreenRecord && (
              <button onClick={onScreenRecord} style={{
                width: '100%', padding: '8px 0', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171',
                marginTop: 4,
              }}>🎥 Screen Record (GIF / Video)</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPanel;
